import { useGetIdentity } from "ra-core";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { flushSync } from "react-dom";
import { useLocation, useNavigate, useNavigationType } from "react-router";
import { toast } from "sonner";

import { DRAFTS_CHANGED_EVENT } from "@/lib/formPersistence/formDraftStorage";
import {
  listInvoiceDrafts,
  listSmsDrafts,
  listTicketReplyDrafts,
} from "@/modules/drafts/draftsRegistry";
import {
  adoptWorkspaceLocation,
  backInWorkspaceTab,
  closeWorkspaceTabs,
  internalPathFromAnchor,
  isWorkOnPage,
  markWorkspaceTabLeft,
  moveWorkspaceTabTo,
  navigationIntent,
  openWorkspaceTab,
  pinWorkspaceTab,
  reconcileWorkspaceTabs,
  tabMatchesDraft,
  workspaceLocationKey,
  type WorkspaceLocation,
  type WorkspaceSession,
  type WorkspaceTab,
} from "@/modules/workspace/workspaceTabs";
import { useWorkspaceTabPreferences } from "@/modules/workspace/workspaceTabPreferences";
import {
  captureWorkspaceScroll,
  restoreWorkspaceScroll,
  type WorkspaceScrollSnapshot,
} from "@/modules/workspace/workspaceScroll";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { WorkspaceTabsContext } from "@/modules/workspace/workspaceTabsContext";

const SESSION_KEY = "nomi.workspace.tabs";
/** The last tabs of any session, offered back when the browser reopens. */
const LAST_SESSION_KEY = "nomi.workspace.tabs.last";
const LAST_SESSION_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const readLastSession = (): WorkspaceSession | null => {
  try {
    const parsed = JSON.parse(
      localStorage.getItem(LAST_SESSION_KEY) ?? "null",
    ) as {
      session?: WorkspaceSession;
      savedAt?: number;
    } | null;
    if (!parsed?.session?.tabs?.length) return null;
    if (Date.now() - Number(parsed.savedAt ?? 0) > LAST_SESSION_MAX_AGE_MS) {
      return null;
    }
    return parsed.session;
  } catch {
    return null;
  }
};
/** Closed tabs you can reopen, newest last. */
const MAX_RECENTLY_CLOSED = 10;

const readSession = (): WorkspaceSession | null => {
  try {
    const parsed = JSON.parse(
      sessionStorage.getItem(SESSION_KEY) ?? "null",
    ) as WorkspaceSession | null;
    if (!parsed?.tabs?.length || typeof parsed.activeId !== "string")
      return null;
    return parsed;
  } catch {
    return null;
  }
};

const hereFrom = (location: {
  pathname: string;
  search: string;
}): WorkspaceLocation => ({
  pathname: location.pathname,
  search: location.search,
});

export const WorkspaceTabsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const location = useLocation();
  const navigationType = useNavigationType();
  /** The page load itself is a POP; only later Back/Forward count. */
  const handledPopKeyRef = useRef(location.key);
  const popTargetRef = useRef<WorkspaceLocation | null>(null);
  const navigate = useNavigate();
  const { identity } = useGetIdentity();
  const memberId = identity?.id;
  const [draftVersion, setDraftVersion] = useState(0);
  /** A new browser session: what was open last time, to offer back. */
  const [lastSession] = useState(() =>
    readSession() ? null : readLastSession(),
  );
  const [session, setSession] = useState<WorkspaceSession>(() =>
    reconcileWorkspaceTabs(readSession(), hereFrom(location)),
  );
  const scrollByTabRef = useRef(
    new Map<string, { key: string; snapshot: WorkspaceScrollSnapshot }>(),
  );
  const restoreForTabRef = useRef<string | null>(null);
  const [confirmCloseIds, setConfirmCloseIds] = useState<string[] | null>(null);
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const locationRef = useRef(location);
  locationRef.current = location;
  const [preferences] = useWorkspaceTabPreferences();
  const preferencesRef = useRef(preferences);
  preferencesRef.current = preferences;
  /**
   * A tab was chosen and the address is on its way there. Until the router
   * moves, the old address must not be written onto the chosen tab.
   */
  const switchingFromKeyRef = useRef<string | null>(null);
  const recentlyClosedRef = useRef<WorkspaceTab[]>([]);
  const [recentlyClosedCount, setRecentlyClosedCount] = useState(0);
  const evictedRef = useRef<WorkspaceTab[]>([]);
  /**
   * Cmd/Ctrl-click on something that is not a link (ticket cards and rows
   * call `navigate`): the page it opens becomes a new tab.
   */
  const modifierClickRef = useRef<{ at: number; from: string } | null>(null);

  const rememberClosed = useCallback((closed: WorkspaceTab[]) => {
    if (!closed.length) return;
    recentlyClosedRef.current = [...recentlyClosedRef.current, ...closed].slice(
      -MAX_RECENTLY_CLOSED,
    );
    setRecentlyClosedCount(recentlyClosedRef.current.length);
  }, []);

  useEffect(() => {
    const bump = () => setDraftVersion((current) => current + 1);
    window.addEventListener(DRAFTS_CHANGED_EVENT, bump);
    return () => window.removeEventListener(DRAFTS_CHANGED_EVENT, bump);
  }, []);

  const drafts = useMemo(
    () =>
      memberId == null
        ? []
        : [
            ...listTicketReplyDrafts(memberId),
            ...listInvoiceDrafts(),
            ...listSmsDrafts(memberId),
          ],
    // draftVersion re-reads localStorage; the lists themselves are not reactive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [memberId, draftVersion],
  );

  const dirtyIds = useMemo(() => {
    const ids = new Set<string>();
    for (const tab of session.tabs) {
      if (drafts.some((draft) => tabMatchesDraft(tab, draft))) ids.add(tab.id);
    }
    return ids;
  }, [drafts, session.tabs]);
  const dirtyRef = useRef(dirtyIds);
  dirtyRef.current = dirtyIds;

  const here = hereFrom(location);
  if (switchingFromKeyRef.current === location.key) {
    // Waiting for the address to reach the tab you chose.
  } else if (
    navigationType === "POP" &&
    location.key !== handledPopKeyRef.current &&
    preferences.opening !== "off"
  ) {
    // Back/Forward stays in this tab (see backInWorkspaceTab).
    switchingFromKeyRef.current = null;
    handledPopKeyRef.current = location.key;
    const back = backInWorkspaceTab(session, here);
    popTargetRef.current = back.target;
    if (back.session !== session) setSession(back.session);
  } else if (navigationType === "POP" && popTargetRef.current) {
    // Waiting for the address to come back into this tab.
  } else if (
    modifierClickRef.current &&
    Date.now() - modifierClickRef.current.at < 800 &&
    workspaceLocationKey(here) !== modifierClickRef.current.from &&
    preferences.opening !== "off"
  ) {
    switchingFromKeyRef.current = null;
    modifierClickRef.current = null;
    // Both pages stay: the one you were on and the one you opened.
    const opened = openWorkspaceTab(
      pinWorkspaceTab(session.tabs, session.activeId),
      here,
      {
        pinned: true,
        after: session.activeId,
        maxTabs: preferences.maxTabs,
        protectIds: [session.activeId],
      },
    );
    evictedRef.current.push(...opened.evicted);
    setSession({
      tabs: opened.tabs,
      activeId: opened.activeId,
      splitId: opened.tabs.some((tab) => tab.id === session.splitId)
        ? session.splitId
        : null,
    });
  } else {
    switchingFromKeyRef.current = null;
    const { evicted, ...next } = adoptWorkspaceLocation(
      session,
      here,
      preferences,
      navigationType !== "REPLACE",
    );
    if (evicted?.length) evictedRef.current.push(...evicted);
    if (
      next.tabs !== session.tabs ||
      next.activeId !== session.activeId ||
      next.splitId !== session.splitId
    ) {
      setSession(next);
    }
  }

  useEffect(() => {
    const target = popTargetRef.current;
    if (!target) return;
    popTargetRef.current = null;
    navigateRef.current(
      { pathname: target.pathname, search: target.search },
      { replace: true },
    );
  }, [location.key]);

  // Typing a draft keeps the tab: the next record will not replace it.
  useEffect(() => {
    const unpinned = session.tabs.filter(
      (tab) => dirtyIds.has(tab.id) && !tab.pinned,
    );
    if (!unpinned.length) return;
    setSession((current) =>
      unpinned.reduce(
        (next, tab) => ({ ...next, tabs: pinWorkspaceTab(next.tabs, tab.id) }),
        current,
      ),
    );
  }, [dirtyIds, session.tabs]);

  // Typing in a form keeps the tab, so the next record opens beside it.
  useEffect(() => {
    const onInput = (event: Event) => {
      if (!isWorkOnPage(event.target)) return;
      const { activeId, tabs } = sessionRef.current;
      if (tabs.find((tab) => tab.id === activeId)?.pinned) return;
      setSession((current) => {
        const tabs = pinWorkspaceTab(current.tabs, current.activeId);
        return tabs === current.tabs ? current : { ...current, tabs };
      });
    };
    document.addEventListener("input", onInput, true);
    return () => document.removeEventListener("input", onInput, true);
  }, []);

  // Tabs turned off: keep only the page you are on.
  useEffect(() => {
    if (preferences.opening !== "off" || session.tabs.length < 2) return;
    setSession((current) => {
      const active =
        current.tabs.find((tab) => tab.id === current.activeId) ??
        current.tabs[0];
      return { tabs: [active], activeId: active.id, splitId: null };
    });
  }, [preferences.opening, session.tabs.length]);

  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    } catch {
      // Private mode: tabs last for this page load only.
    }
    try {
      localStorage.setItem(
        LAST_SESSION_KEY,
        JSON.stringify({ session, savedAt: Date.now() }),
      );
    } catch {
      // Private mode: nothing to offer next time.
    }
  }, [session]);

  // The tab you leave remembers when, so news after that shows on it.
  const previousActiveRef = useRef(session.activeId);
  useEffect(() => {
    const left = previousActiveRef.current;
    previousActiveRef.current = session.activeId;
    if (left === session.activeId) return;
    setSession((current) => ({
      ...current,
      tabs: markWorkspaceTabLeft(current.tabs, left),
    }));
  }, [session.activeId]);

  // Back on a tab you chose: put its scroll where you left it.
  useEffect(() => {
    const tabId = restoreForTabRef.current;
    if (!tabId || switchingFromKeyRef.current === location.key) return;
    restoreForTabRef.current = null;
    const saved = scrollByTabRef.current.get(tabId);
    const tab = sessionRef.current.tabs.find((item) => item.id === tabId);
    if (!saved || !tab || saved.key !== workspaceLocationKey(tab)) return;
    return restoreWorkspaceScroll(saved.snapshot);
  }, [location.key]);

  /** Show `next` (its active tab already chosen) and move the address there. */
  const showSession = useCallback((next: WorkspaceSession) => {
    const destination = next.tabs.find((tab) => tab.id === next.activeId);
    const current = locationRef.current;
    const moves =
      destination &&
      workspaceLocationKey(destination) !==
        workspaceLocationKey(hereFrom(current));
    const leaving = sessionRef.current.tabs.find(
      (tab) => tab.id === sessionRef.current.activeId,
    );
    if (moves && leaving && leaving.id !== next.activeId) {
      scrollByTabRef.current.set(leaving.id, {
        key: workspaceLocationKey(leaving),
        snapshot: captureWorkspaceScroll(),
      });
      restoreForTabRef.current = next.activeId;
    }
    switchingFromKeyRef.current = moves ? current.key : null;
    flushSync(() => setSession(next));
    if (moves) {
      navigateRef.current({
        pathname: destination.pathname,
        search: destination.search,
      });
    }
  }, []);

  const openDestination = useCallback(
    (destination: WorkspaceLocation) => {
      const current = sessionRef.current;
      // Cmd/Ctrl-click keeps both: the page you are on and the one you open.
      const opened = openWorkspaceTab(
        pinWorkspaceTab(current.tabs, current.activeId),
        destination,
        {
          pinned: true,
          after: current.activeId,
          maxTabs: preferencesRef.current.maxTabs,
          protectIds: [current.activeId, current.splitId].filter(
            (id): id is string => Boolean(id),
          ),
        },
      );
      evictedRef.current.push(...opened.evicted);
      showSession({
        tabs: opened.tabs,
        activeId: opened.activeId,
        splitId: opened.tabs.some((tab) => tab.id === current.splitId)
          ? current.splitId
          : null,
      });
    },
    [showSession],
  );

  // Say which tab the limit closed, with a way back.
  useEffect(() => {
    // Strict Mode renders twice: count each tab once.
    const evicted = [
      ...new Map(
        evictedRef.current.splice(0).map((tab) => [tab.id, tab]),
      ).values(),
    ];
    if (!evicted.length) return;
    rememberClosed(evicted);
    const names = evicted.map((tab) => `“${tab.title}”`).join(", ");
    toast(`Closed ${names} to stay at ${preferences.maxTabs} tabs`, {
      action: {
        label: "Reopen",
        onClick: () => reopenRef.current(),
      },
    });
  });

  useEffect(() => {
    const clickTarget = (event: Event) => {
      const node = event.target;
      const element =
        node instanceof Element
          ? node
          : node instanceof Node
            ? node.parentElement
            : null;
      return element?.closest("a[href]") ?? null;
    };

    const take = (event: MouseEvent, modifier: boolean) => {
      if (event.defaultPrevented || event.shiftKey || event.altKey) return;
      const anchor = clickTarget(event);
      if (!(anchor instanceof HTMLAnchorElement)) {
        modifierClickRef.current = null;
        if (modifier && event.button === 0) {
          const current = sessionRef.current;
          const active = current.tabs.find(
            (tab) => tab.id === current.activeId,
          );
          modifierClickRef.current = active
            ? { at: Date.now(), from: workspaceLocationKey(active) }
            : null;
        }
        return;
      }
      const destination = internalPathFromAnchor(
        anchor,
        window.location.origin,
      );
      const current = sessionRef.current;
      const active =
        current.tabs.find((tab) => tab.id === current.activeId) ??
        current.tabs[0];
      if (!active) return;
      const intent = navigationIntent({
        modifier,
        destination,
        current: active,
        dirty: dirtyRef.current.has(active.id),
        opening: preferencesRef.current.opening,
      });
      if (intent !== "open-tab" || !destination) return;
      event.preventDefault();
      event.stopPropagation();
      openDestination(destination);
    };

    const onClick = (event: MouseEvent) => {
      if (event.button !== 0) return;
      const mac = /Mac|iPhone|iPad/.test(navigator.platform);
      const modifier = mac ? event.metaKey : event.metaKey || event.ctrlKey;
      take(event, modifier);
    };
    const onAux = (event: MouseEvent) => {
      if (event.button !== 1) return;
      take(event, true);
    };
    document.addEventListener("click", onClick, true);
    document.addEventListener("auxclick", onAux, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("auxclick", onAux, true);
    };
  }, [openDestination]);

  const selectTab = useCallback(
    (id: string) => {
      const current = sessionRef.current;
      if (!current.tabs.some((item) => item.id === id)) return;
      showSession({
        ...current,
        activeId: id,
        splitId: current.splitId === id ? null : current.splitId,
      });
    },
    [showSession],
  );

  const closeTabs = useCallback(
    (ids: string[], confirmed = false) => {
      if (!confirmed && ids.some((id) => dirtyRef.current.has(id))) {
        setConfirmCloseIds(ids);
        return;
      }
      const current = sessionRef.current;
      const closed = closeWorkspaceTabs(
        current.tabs,
        current.activeId,
        ids,
        current.splitId,
      );
      if (!closed.closed.length) return;
      rememberClosed(closed.closed);
      showSession({
        tabs: closed.tabs,
        activeId: closed.activeId,
        splitId: closed.splitId,
      });
    },
    [showSession, rememberClosed],
  );

  const closeTab = useCallback((id: string) => closeTabs([id]), [closeTabs]);
  const confirmTitles = (confirmCloseIds ?? [])
    .map((id) => session.tabs.find((tab) => tab.id === id))
    .filter((tab): tab is WorkspaceTab => Boolean(tab && dirtyIds.has(tab.id)))
    .map((tab) => `“${tab.title}”`)
    .join(", ");

  const closeOtherTabs = useCallback(
    (id: string) =>
      closeTabs(
        sessionRef.current.tabs
          .filter((tab) => tab.id !== id)
          .map((tab) => tab.id),
      ),
    [closeTabs],
  );

  const closeTabsToRight = useCallback(
    (id: string) => {
      const tabs = sessionRef.current.tabs;
      const index = tabs.findIndex((tab) => tab.id === id);
      if (index < 0) return;
      closeTabs(tabs.slice(index + 1).map((tab) => tab.id));
    },
    [closeTabs],
  );

  const duplicateTab = useCallback(
    (id: string) => {
      const current = sessionRef.current;
      const source = current.tabs.find((tab) => tab.id === id);
      if (!source) return;
      const opened = openWorkspaceTab(current.tabs, source, {
        pinned: true,
        after: id,
        maxTabs: preferencesRef.current.maxTabs,
        protectIds: [current.activeId, id],
      });
      evictedRef.current.push(...opened.evicted);
      showSession({
        ...current,
        activeId: opened.activeId,
        tabs: opened.tabs.map((tab) =>
          tab.id === opened.activeId
            ? { ...tab, title: source.title, history: source.history }
            : tab,
        ),
      });
    },
    [showSession],
  );

  const moveTab = useCallback((fromId: string, toId: string) => {
    setSession((current) => {
      const tabs = moveWorkspaceTabTo(current.tabs, fromId, toId);
      return tabs === current.tabs ? current : { ...current, tabs };
    });
  }, []);

  // A new browser session: offer the tabs that were open last time.
  useEffect(() => {
    if (!lastSession || preferencesRef.current.opening === "off") return;
    const here = workspaceLocationKey(hereFrom(locationRef.current));
    const extra = lastSession.tabs.filter(
      (tab) => workspaceLocationKey(tab) !== here,
    );
    if (!extra.length) return;
    toast(
      `Restore ${extra.length} tab${extra.length === 1 ? "" : "s"} from last time?`,
      {
        duration: 15_000,
        action: {
          label: "Restore",
          onClick: () =>
            setSession((current) => {
              const active =
                current.tabs.find((tab) => tab.id === current.activeId) ??
                current.tabs[0];
              const keep = preferencesRef.current.maxTabs - 1;
              const restored = extra
                .filter(
                  (tab) => !current.tabs.some((item) => item.id === tab.id),
                )
                .slice(-keep);
              return {
                tabs: [...restored, active],
                activeId: active.id,
                splitId: null,
              };
            }),
        },
      },
    );
    // Once, on the first load of this browser session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pinTab = useCallback((id: string) => {
    setSession((current) => {
      const tabs = pinWorkspaceTab(current.tabs, id);
      return tabs === current.tabs ? current : { ...current, tabs };
    });
  }, []);

  const reopenClosedTab = useCallback(() => {
    const tab = recentlyClosedRef.current.pop();
    setRecentlyClosedCount(recentlyClosedRef.current.length);
    if (!tab) return;
    const current = sessionRef.current;
    const opened = openWorkspaceTab(current.tabs, tab, {
      pinned: true,
      after: current.activeId,
      maxTabs: preferencesRef.current.maxTabs,
      protectIds: [current.activeId],
    });
    evictedRef.current.push(...opened.evicted);
    showSession({
      ...current,
      activeId: opened.activeId,
      tabs: opened.tabs.map((item) =>
        item.id === opened.activeId
          ? { ...item, title: tab.title, history: tab.history }
          : item,
      ),
    });
  }, [showSession]);
  const reopenRef = useRef(reopenClosedTab);
  reopenRef.current = reopenClosedTab;

  const rememberTitle = useCallback((title: string) => {
    const trimmed = title.trim();
    if (!trimmed) return;
    setSession((current) => {
      const here = hereFrom(locationRef.current);
      // The title belongs to the page on screen: the active tab.
      const tab = current.tabs.find((item) => item.id === current.activeId);
      if (
        !tab ||
        tab.title === trimmed ||
        workspaceLocationKey(tab) !== workspaceLocationKey(here)
      ) {
        return current;
      }
      return {
        ...current,
        tabs: current.tabs.map((item) =>
          item.id === tab.id ? { ...item, title: trimmed } : item,
        ),
      };
    });
  }, []);

  const value = useMemo(
    () => ({
      tabs: session.tabs,
      activeId: session.activeId,
      dirtyIds,
      enabled: preferences.opening !== "off",
      canReopen: recentlyClosedCount > 0,
      openInNewTab: openDestination,
      selectTab,
      closeTab,
      closeOtherTabs,
      closeTabsToRight,
      pinTab,
      duplicateTab,
      moveTab,
      reopenClosedTab,
      rememberTitle,
    }),
    [
      session.tabs,
      session.activeId,
      dirtyIds,
      preferences.opening,
      recentlyClosedCount,
      openDestination,
      selectTab,
      closeTab,
      closeOtherTabs,
      closeTabsToRight,
      pinTab,
      duplicateTab,
      moveTab,
      reopenClosedTab,
      rememberTitle,
    ],
  );

  return (
    <WorkspaceTabsContext.Provider value={value}>
      {children}
      <Dialog
        open={Boolean(confirmCloseIds)}
        onOpenChange={(open) => !open && setConfirmCloseIds(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Close with an unsent draft?</DialogTitle>
            <DialogDescription>
              {confirmTitles || "This tab"} has a draft that was not sent. It
              stays in Drafts, so you can finish it later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmCloseIds(null)}
            >
              Keep open
            </Button>
            <Button
              type="button"
              onClick={() => {
                const ids = confirmCloseIds ?? [];
                setConfirmCloseIds(null);
                closeTabs(ids, true);
              }}
            >
              Close tab
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </WorkspaceTabsContext.Provider>
  );
};
